import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #formatVariantMoney(amount) {
    const currency = window.Shopify?.currency?.active || 'USD';
    const format = window.Shopify?.money_format || '${{amount}}';

    return formatMoney(Number(amount || 0), format, currency);
  }

  #setTextIfChanged(node, value) {
    if (!node) return;

    const nextValue = value ?? '';
    if (node.textContent !== nextValue) {
      node.textContent = nextValue;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (event.detail.data.localUpdate && event.detail.resource) {
      const { priceContainer, volumePricingNote } = this.refs;
      const regularPriceBlock = priceContainer?.querySelector('.price__regular');
      const salePriceBlock = priceContainer?.querySelector('.price__sale');
      const regularPrice = regularPriceBlock?.querySelector('.price');
      const salePrice = salePriceBlock?.querySelector('.price-item--sale.price') || salePriceBlock?.querySelector('.price');
      const compareAtPrice = priceContainer?.querySelector('.compare-at-price');
      const formattedPrice = this.#formatVariantMoney(event.detail.resource.price);
      const formattedCompareAtPrice = this.#formatVariantMoney(event.detail.resource.compare_at_price);
      const showComparePrice = Number(event.detail.resource.compare_at_price) > Number(event.detail.resource.price);

      this.#setTextIfChanged(regularPrice, formattedPrice);
      this.#setTextIfChanged(salePrice, formattedPrice);
      this.#setTextIfChanged(compareAtPrice, showComparePrice ? formattedCompareAtPrice : '');

      if (regularPriceBlock) {
        regularPriceBlock.classList.toggle('price__hidden', showComparePrice);
      }

      if (salePriceBlock) {
        salePriceBlock.classList.toggle('price__hidden', !showComparePrice);
      }
      volumePricingNote?.remove();

      const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
      const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
      if (installmentsInput) {
        installmentsInput.value = event.detail.resource.id ?? '';
        installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
